import { useContext, useMemo, useState } from 'react';

import { Button, useDisclosure } from '@chakra-ui/react';
import { toast } from 'react-toastify';

import { DealStateEnum } from '../../../../gql/dealGql';
import {
  DealMediaTypeEnum,
  LendingNetwork,
  useCreditDecisionDataQuery,
  useMediaQuery,
  useUploadDocumentsToCudlMutation,
} from '../../../../gql/generated/graphql';

import Modal from '../../../shared/Modal';
import Tooltip from '../../../shared/Tooltip';

import { isCUDL } from '../../../../constants/creditDecisions';
import { DealContext } from '../../../../libs/DealContext';
import { logger } from '../../../../libs/Logger';
import { CUDLFile, dealMediaToFile } from '../../../../utils/media';
import DocumentsTable from '../../../DocumentsTable/DocumentsTable';

type UploadDocumentsToCUDLButtonProps = {
  width?: string;
};

const UploadDocumentsToCUDLButton = ({ width }: UploadDocumentsToCUDLButtonProps) => {
  const { deal } = useContext(DealContext);

  const creditDecisionGql = useCreditDecisionDataQuery({
    skip: !deal.financial_info?.selected_credit_decision_id,
    variables: {
      id: Number(deal.financial_info?.selected_credit_decision_id),
    },
  });
  const selectedCreditDecisionIsCUDL = useMemo(
    () => isCUDL(creditDecisionGql.data?.creditDecisionV2?.provider_decision_data),
    [creditDecisionGql.data?.creditDecisionV2],
  );

  const mediaGql = useMediaQuery({
    skip: !deal.id,
    variables: {
      dealId: deal.id ?? -1,
    },
    fetchPolicy: 'cache-and-network',
  });
  const media = mediaGql.data?.media;

  const signedDocFiles = useMemo(
    () =>
      media?.reduce<CUDLFile[]>((acc, mediaItem) => {
        if (mediaItem != null && mediaItem.type === DealMediaTypeEnum.SignedDocs) {
          acc.push(dealMediaToFile(mediaItem));
        }
        return acc;
      }, []) ?? [],
    [media],
  );
  const noSignedDocFiles = useMemo(() => signedDocFiles.length === 0, [signedDocFiles]);

  const alreadyUploadedFileNames = useMemo(
    () =>
      signedDocFiles.reduce<string[]>((acc, file) => {
        if (file.filename != null && file.cudlDocumentId != null) {
          acc.push(file.filename);
        }
        return acc;
      }, []),
    [signedDocFiles],
  );

  const [uploadDocumentsToCUDL, { loading: uploadingDocumentsToCUDL }] =
    useUploadDocumentsToCudlMutation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [selectedFilesIndexes, setSelectedFilesIndexes] = useState<number[]>([]);

  const closeModal = () => {
    setSelectedFilesIndexes([]);
    onClose();
  };

  const handleUploadDocumentsToCUDL = async () => {
    if (!deal.id) {
      closeModal();
      return;
    }

    try {
      const selectedKeys = signedDocFiles.reduce<string[]>((acc, file, index) => {
        if (selectedFilesIndexes.includes(index) && file.key != null) {
          acc.push(file.key);
        }
        return acc;
      }, []);

      const { data } = await uploadDocumentsToCUDL({
        variables: {
          dealId: deal.id,
          keys: selectedKeys,
        },
      });

      if (
        !data?.uploadDocumentsToCUDL?.uploadedDocuments?.length &&
        !data?.uploadDocumentsToCUDL?.errors?.length
      ) {
        toast.error('No documents were uploaded to CUDL');
      }
      if (data?.uploadDocumentsToCUDL?.uploadedDocuments.length) {
        toast.success(
          `Documents uploaded to CUDL successfully:\n${data.uploadDocumentsToCUDL.uploadedDocuments.join(
            '\n',
          )}`,
        );

        mediaGql.refetch();
      }
      if (data?.uploadDocumentsToCUDL?.errors.length) {
        toast.error(
          `Failed to upload documents to CUDL:\n${data?.uploadDocumentsToCUDL?.errors?.join('\n')}`,
        );
      }
    } catch (e) {
      logger.error('UploadDocumentsToCUDLButton.tsx', '', null, e);
      toast.error(
        `Failed to upload documents to CUDL: ${e instanceof Error ? e.message : 'Unknown error'}`,
      );
    } finally {
      closeModal();
    }
  };

  return selectedCreditDecisionIsCUDL && deal.state === DealStateEnum.Signed ? (
    <>
      <Tooltip errors={noSignedDocFiles ? ['No Signed Docs to upload'] : undefined}>
        <Button
          name="open-upload-documents-to-cudl-modal"
          onClick={onOpen}
          width={width}
          variant="secondary"
          disabled={noSignedDocFiles}
        >
          UPLOAD TO CUDL
        </Button>
      </Tooltip>
      <Modal
        title="Upload Documents to CUDL"
        isOpen={isOpen}
        onClose={closeModal}
        canDismiss={!uploadingDocumentsToCUDL}
        closeOnOverlayClick={false}
        size="3xl"
        leftButtons={
          <Button variant="warning" onClick={closeModal} isDisabled={uploadingDocumentsToCUDL}>
            CANCEL
          </Button>
        }
        rightButtons={
          <Button
            name="upload-documents-to-cudl"
            isLoading={uploadingDocumentsToCUDL}
            loadingText="UPLOAD TO CUDL"
            isDisabled={!selectedFilesIndexes.length}
            onClick={() => {
              handleUploadDocumentsToCUDL();
            }}
          >
            UPLOAD TO CUDL
          </Button>
        }
      >
        <DocumentsTable
          infoText="Select the documents you would like to upload to CUDL."
          files={signedDocFiles}
          numDocsInProvider={alreadyUploadedFileNames.length}
          providerName={LendingNetwork.Cudl}
          existingFiles={alreadyUploadedFileNames}
          selectedFilesIndexes={selectedFilesIndexes}
          setSelectedFilesIndexes={setSelectedFilesIndexes}
          modal="upload"
        />
      </Modal>
    </>
  ) : null;
};

export default UploadDocumentsToCUDLButton;
